import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getManagerTasks, saveManagerTask, completeTask, exportScheduledJobs, emailScheduledJobsReport, bulkUpdateScheduledJobs, GetManagerTasksOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  CheckSquare, Plus, Trash2, Pencil, Loader2, Clock, AlertTriangle, CheckCircle2,
  Download, Mail, Printer, Filter, X, Calendar, FileText, SquareCheckBig, Search, ChevronDown, ChevronUp
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

type TasksData = GetManagerTasksOutputType;
type Task = TasksData['tasks'][0];

const FREQUENCIES = ['Daily', 'Weekly', 'Monthly', 'Yearly', 'One-off'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const CATEGORIES = ['Operations', 'Safety', 'Cleaning', 'Admin', 'Staff', 'Other'];
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];
const PRIORITY_COLORS: Record<string, string> = {
  Urgent: 'bg-destructive/10 text-destructive border-destructive/30',
  High: 'bg-accent/20 text-accent-foreground border-accent/30',
  Medium: 'bg-muted text-muted-foreground border-border',
  Low: 'bg-muted/50 text-muted-foreground border-border/50',
};
const CATEGORY_COLORS: Record<string, string> = {
  Operations: 'bg-primary/10 text-primary', Safety: 'bg-destructive/10 text-destructive',
  Cleaning: 'bg-primary/10 text-primary', Admin: 'bg-muted text-muted-foreground',
  Staff: 'bg-accent/10 text-accent-foreground', Other: 'bg-muted text-muted-foreground',
};

const emptyForm = {
  taskName: '', description: '', frequency: 'Weekly', dayOfWeek: '', category: 'Operations',
  priority: 'Medium', assignedToName: '', reminderEmail: '', reminderPhone: '',
  sendEmail: false, sendSms: false, leadTimeDays: 1,
};

// ——— Task Card ———
function TaskCard({ task, selected, onToggle, onComplete, onEdit, onDelete, isManager }: {
  task: Task; selected: boolean; onToggle: () => void; onComplete: (id: string) => void;
  onEdit: (t: Task) => void; onDelete: (id: string) => void; isManager: boolean;
}) {
  const [completing, setCompleting] = useState(false);
  const [completeNotes, setCompleteNotes] = useState('');
  const [completeOpen, setCompleteOpen] = useState(false);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await completeTask({ taskId: task.id, notes: completeNotes, sendReminders: task.sendEmail || task.sendSms });
      toast.success(`"${task.taskName}" marked complete`);
      setCompleteOpen(false);
      setCompleteNotes('');
      onComplete(task.id);
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setCompleting(false); }
  };

  return (
    <div className={`bg-card border rounded-xl p-3 transition-all ${task.isOverdue ? 'border-destructive/40' : 'border-border'} ${selected ? 'ring-2 ring-primary/50' : ''}`}>
      <div className="flex items-start gap-2">
        {isManager && (
          <button onClick={onToggle} className={`mt-1 shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${selected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/30 hover:border-primary'}`}>
            {selected && <CheckSquare className="w-3 h-3" />}
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span className="text-sm font-semibold text-foreground">{task.taskName}</span>
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-primary/20 text-primary font-normal">Native</Badge>
            {task.isOverdue && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">OVERDUE</Badge>}
            {!task.isOverdue && task.isDueToday && <Badge className="text-[10px] px-1.5 py-0 bg-accent text-accent-foreground">DUE TODAY</Badge>}
            {!task.isOverdue && !task.isDueToday && task.isDueSoon && <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/30 text-primary">DUE SOON</Badge>}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
            {task.priority && <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${PRIORITY_COLORS[task.priority] || ''}`}>{task.priority}</span>}
            {task.category && <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[task.category] || 'bg-muted text-muted-foreground'}`}>{task.category}</span>}
            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{task.frequency}{task.dayOfWeek ? ` · ${task.dayOfWeek}` : ''}</span>
          </div>
          {task.description && <p className="text-xs text-muted-foreground mb-1 line-clamp-1">{task.description}</p>}
          <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground">
            {task.assignedToName && <span>👤 {task.assignedToName}</span>}
            {task.lastCompletedAt ? (
              <span>Last: {format(parseISO(task.lastCompletedAt), 'd MMM')}{task.lastCompletedBy ? ` by ${task.lastCompletedBy}` : ''}</span>
            ) : <span className="text-destructive/80 font-medium">Never completed</span>}
            {task.leadTimeDays && task.leadTimeDays > 0 && <span>🔔 {task.leadTimeDays}d lead</span>}
            <span className={task.isOverdue ? 'text-destructive font-medium' : ''}>
              {task.isOverdue ? `${Math.abs(task.daysUntilDue)}d overdue` : task.daysUntilDue === 0 ? 'Due today' : `Due in ${task.daysUntilDue}d`}
            </span>
            {task.completionCount > 0 && <span className="text-primary">✓ {task.completionCount}x done</span>}
          </div>
        </div>
        {isManager && (
          <div className="flex flex-col gap-1 shrink-0">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(task)}><Pencil className="w-3.5 h-3.5" /></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Remove this job?</AlertDialogTitle></AlertDialogHeader>
                <p className="text-sm text-muted-foreground">This will deactivate "{task.taskName}". Completion history is preserved.</p>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(task.id)} className="bg-destructive text-destructive-foreground">Remove</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {isManager && (
        <div className="mt-2.5 pt-2.5 border-t border-border/50">
          <Button size="sm" className="w-full gap-1.5 h-8 text-xs" onClick={() => setCompleteOpen(true)}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Mark Complete
          </Button>
        </div>
      )}

      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Complete: {task.taskName}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Textarea value={completeNotes} onChange={e => setCompleteNotes(e.target.value)} rows={2} placeholder="Notes about this completion…" />
            <Button onClick={handleComplete} disabled={completing} className="w-full">
              {completing ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Saving…</> : <><CheckCircle2 className="w-3.5 h-3.5 mr-2" />Mark as Done</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ——— Date Presets ———
const DATE_PRESETS = [
  { label: 'This Week', value: 'this-week' },
  { label: 'Next 7 Days', value: 'next-7' },
  { label: 'Next 30 Days', value: 'next-30' },
  { label: 'This Month', value: 'this-month' },
];

// ——— Main Page ———
export default function ManagerTasks() {
  const { user } = useAuth();
  const isManager = user?.role === 'Manager' || user?.role === 'Admin' || user?.role === 'Owner';

  const [tasks, setTasks] = useState<Task[]>([]);
  const [counts, setCounts] = useState({ total: 0, overdue: 0, dueToday: 0, dueSoon: 0, upToDate: 0 });
  const [assignees, setAssignees] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [frequencyFilter, setFrequencyFilter] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Form
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [form, setForm] = useState(emptyForm);

  // Bulk
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Export
  const [exportOpen, setExportOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailNotes, setEmailNotes] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [exporting, setExporting] = useState(false);

  const hasActiveFilters = categoryFilter || priorityFilter || assigneeFilter || frequencyFilter || statusFilter !== 'all' || datePreset;

  const load = useCallback(() => {
    setLoading(true);
    getManagerTasks({
      category: categoryFilter || undefined,
      priority: priorityFilter || undefined,
      assignee: assigneeFilter || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      frequency: frequencyFilter || undefined,
    }).then(d => {
      setTasks(d.tasks);
      setCounts(d.counts);
      setAssignees(d.assignees);
      setCategories(d.categories);
    }).catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [categoryFilter, priorityFilter, assigneeFilter, statusFilter, frequencyFilter]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const displayed = useMemo(() => {
    let filtered = tasks;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(t => t.taskName.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q) || t.assignedToName?.toLowerCase().includes(q));
    }
    if (datePreset) {
      const now = new Date();
      filtered = filtered.filter(t => {
        const due = new Date(t.nextDueAt);
        const days = Math.ceil((due.getTime() - now.getTime()) / 864e5);
        if (datePreset === 'this-week') return days <= 7 || t.isOverdue;
        if (datePreset === 'next-7') return days <= 7 || t.isOverdue;
        if (datePreset === 'next-30') return days <= 30 || t.isOverdue;
        if (datePreset === 'this-month') {
          return (due.getMonth() === now.getMonth() && due.getFullYear() === now.getFullYear()) || t.isOverdue;
        }
        return true;
      });
    }
    return filtered;
  }, [tasks, searchQuery, datePreset]);

  const clearFilters = () => {
    setStatusFilter('all'); setCategoryFilter(''); setPriorityFilter('');
    setAssigneeFilter(''); setFrequencyFilter(''); setSearchQuery(''); setDatePreset('');
  };

  const openEdit = (task: Task) => {
    setEditingTask(task);
    setForm({
      taskName: task.taskName, description: task.description || '', frequency: task.frequency || 'Weekly',
      dayOfWeek: task.dayOfWeek || '', category: task.category || 'Operations', priority: task.priority || 'Medium',
      assignedToName: task.assignedToName || '', reminderEmail: task.reminderEmail || '',
      reminderPhone: task.reminderPhone || '', sendEmail: task.sendEmail || false,
      sendSms: task.sendSms || false, leadTimeDays: task.leadTimeDays ?? 1,
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.taskName.trim()) { toast.error('Enter a task name'); return; }
    setSaving(true);
    try {
      await saveManagerTask({ id: editingTask?.id, ...form });
      toast.success(editingTask ? 'Job updated' : 'Job added');
      setFormOpen(false); setEditingTask(null); setForm(emptyForm);
      load();
    } catch (e: any) { toast.error(e.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await saveManagerTask({ deleteId: id }); toast.success('Job removed'); load(); }
    catch { toast.error('Failed to remove'); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const selectAll = () => {
    if (selectedIds.size === displayed.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(displayed.map(t => t.id)));
  };

  const handleBulk = async () => {
    if (selectedIds.size === 0) return;
    setBulkProcessing(true);
    try {
      await bulkUpdateScheduledJobs({
        taskIds: Array.from(selectedIds),
        action: bulkAction as 'complete' | 'reassign' | 'deactivate',
        assignedToName: bulkAction === 'reassign' ? bulkAssignee : undefined,
      });
      toast.success(`${selectedIds.size} job(s) ${bulkAction === 'complete' ? 'completed' : bulkAction === 'reassign' ? 'reassigned' : 'deactivated'}`);
      setSelectedIds(new Set()); setBulkAction('');
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setBulkProcessing(false); }
  };

  const handleExport = async (fmt: 'pdf' | 'csv', type: 'due-now' | 'monthly') => {
    setExporting(true);
    try {
      const result = await exportScheduledJobs({ format: fmt, reportType: type, month: new Date().getMonth(), year: new Date().getFullYear() });
      if (result.csv) {
        const blob = new Blob([result.csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = result.filename; a.click();
        URL.revokeObjectURL(url);
      } else if (result.url) {
        window.open(result.url, '_blank');
      }
      toast.success('Export ready');
      setExportOpen(false);
    } catch (e: any) { toast.error(e.message); }
    finally { setExporting(false); }
  };

  const handleEmail = async (type: 'due-now' | 'summary' | 'monthly') => {
    if (!emailTo.trim()) { toast.error('Enter an email address'); return; }
    setEmailSending(true);
    try {
      await emailScheduledJobsReport({ recipientEmail: emailTo, reportType: type, notes: emailNotes });
      toast.success('Report emailed!');
      setEmailOpen(false); setEmailTo(''); setEmailNotes('');
    } catch (e: any) { toast.error(e.message); }
    finally { setEmailSending(false); }
  };

  if (!isManager) return (
    <div className="px-4 py-16 text-center text-muted-foreground">
      <CheckSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
      <p className="text-sm font-medium">Manager access required</p>
      <p className="text-xs mt-1">Scheduled Jobs is available to Managers, Admins and Owners only.</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background print:bg-white">
      {/* Header */}
      <div className="bg-card border-b border-border print:hidden sticky top-0 z-10">
        <div className="px-4 md:px-8 py-3 md:py-5 max-w-5xl mx-auto">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 md:w-11 md:h-11 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                <CheckSquare className="w-4.5 h-4.5 md:w-5 md:h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base md:text-xl font-bold text-foreground truncate">Scheduled Jobs</h1>
                <p className="text-[10px] md:text-xs text-muted-foreground">Native recurring tasks · Source: Manual</p>
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" variant="outline" className="gap-1 text-xs h-8 px-2 md:px-3" onClick={() => setExportOpen(true)}>
                <Download className="w-3.5 h-3.5" /><span className="hidden md:inline">Export</span>
              </Button>
              <Button size="sm" variant="outline" className="gap-1 text-xs h-8 px-2 md:px-3" onClick={() => setEmailOpen(true)}>
                <Mail className="w-3.5 h-3.5" /><span className="hidden md:inline">Email</span>
              </Button>
              <Button size="sm" variant="outline" className="gap-1 text-xs h-8 px-2 md:px-3 hidden sm:flex" onClick={() => window.print()}>
                <Printer className="w-3.5 h-3.5" /><span className="hidden md:inline">Print</span>
              </Button>
              <Button size="sm" className="gap-1 text-xs h-8 px-2 md:px-3" onClick={() => { setEditingTask(null); setForm(emptyForm); setFormOpen(true); }}>
                <Plus className="w-3.5 h-3.5" /><span className="hidden sm:inline">Add Job</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 py-4 md:py-6 max-w-5xl mx-auto space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-5 gap-1.5 md:gap-3 print:grid-cols-5">
          {[
            { label: 'Total', value: counts.total, cls: 'text-foreground' },
            { label: 'Overdue', value: counts.overdue, cls: 'text-destructive', onClick: () => setStatusFilter('overdue') },
            { label: 'Today', value: counts.dueToday, cls: 'text-accent-foreground', onClick: () => setStatusFilter('due-today') },
            { label: 'Soon', value: counts.dueSoon, cls: 'text-primary', onClick: () => setStatusFilter('due-soon') },
            { label: 'OK', value: counts.upToDate, cls: 'text-primary', onClick: () => setStatusFilter('up-to-date') },
          ].map(s => (
            <button key={s.label} onClick={s.onClick} className="border border-border rounded-lg md:rounded-xl p-1.5 md:p-3 text-center bg-card hover:bg-muted/20 transition-colors">
              <p className={`text-base md:text-2xl font-bold ${s.cls}`}>{s.value}</p>
              <p className="text-[9px] md:text-xs text-muted-foreground">{s.label}</p>
            </button>
          ))}
        </div>

        {/* Overdue warning */}
        {counts.overdue > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-xl px-3 md:px-4 py-2.5 flex items-center gap-2 print:hidden">
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
            <p className="text-xs md:text-sm text-destructive font-medium">{counts.overdue} job{counts.overdue > 1 ? 's are' : ' is'} overdue</p>
          </div>
        )}

        {/* Filter bar */}
        <div className="print:hidden space-y-3">
          {/* Status pills - horizontal scroll on mobile */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
            {['all', 'overdue', 'due-today', 'due-soon', 'up-to-date'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`text-[11px] px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors ${statusFilter === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}>
                {s === 'all' ? `All (${counts.total})` : s === 'overdue' ? `Overdue (${counts.overdue})` : s === 'due-today' ? `Today (${counts.dueToday})` : s === 'due-soon' ? `Soon (${counts.dueSoon})` : `OK (${counts.upToDate})`}
              </button>
            ))}
            <button onClick={() => setShowFilters(!showFilters)} className={`text-[11px] px-2.5 py-1 rounded-full border flex items-center gap-1 whitespace-nowrap transition-colors ${showFilters || hasActiveFilters ? 'bg-primary/10 border-primary/30 text-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}>
              <Filter className="w-3 h-3" />More{hasActiveFilters ? ' ●' : ''}
              {showFilters ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
            </button>
            {hasActiveFilters && <button onClick={clearFilters} className="text-[11px] text-destructive hover:underline flex items-center gap-0.5 whitespace-nowrap"><X className="w-3 h-3" />Clear</button>}
          </div>

          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-muted/30 rounded-xl border border-border">
              <div>
                <label className="text-[10px] text-muted-foreground block mb-1">Category</label>
                <Select value={categoryFilter || '_all'} onValueChange={v => setCategoryFilter(v === '_all' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All</SelectItem>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground block mb-1">Priority</label>
                <Select value={priorityFilter || '_all'} onValueChange={v => setPriorityFilter(v === '_all' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All</SelectItem>
                    {PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground block mb-1">Assignee</label>
                <Select value={assigneeFilter || '_all'} onValueChange={v => setAssigneeFilter(v === '_all' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All</SelectItem>
                    {assignees.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground block mb-1">Frequency</label>
                <Select value={frequencyFilter || '_all'} onValueChange={v => setFrequencyFilter(v === '_all' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All</SelectItem>
                    {FREQUENCIES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground block mb-1">Date Range</label>
                <Select value={datePreset || '_all'} onValueChange={v => setDatePreset(v === '_all' ? '' : v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">All Dates</SelectItem>
                    {DATE_PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
            <Input placeholder="Search jobs…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-9 text-sm pl-8" />
          </div>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap p-3 bg-primary/5 border border-primary/20 rounded-xl print:hidden">
            <SquareCheckBig className="w-4 h-4 text-primary shrink-0" />
            <span className="text-xs md:text-sm font-medium text-foreground">{selectedIds.size} selected</span>
            <Select value={bulkAction || 'pick'} onValueChange={setBulkAction}>
              <SelectTrigger className="h-8 w-36 md:w-40 text-xs"><SelectValue placeholder="Action…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pick" disabled>Choose action…</SelectItem>
                <SelectItem value="complete">✅ Mark Complete</SelectItem>
                <SelectItem value="reassign">👤 Reassign</SelectItem>
                <SelectItem value="deactivate">🗑 Deactivate</SelectItem>
              </SelectContent>
            </Select>
            {bulkAction === 'reassign' && <Input placeholder="New assignee…" className="h-8 text-xs w-32 md:w-40" value={bulkAssignee} onChange={e => setBulkAssignee(e.target.value)} />}
            <Button size="sm" className="h-8 text-xs" disabled={!bulkAction || bulkAction === 'pick' || bulkProcessing} onClick={handleBulk}>
              {bulkProcessing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
            </Button>
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setSelectedIds(new Set())}>Cancel</Button>
          </div>
        )}

        {/* Select all */}
        {isManager && displayed.length > 0 && !loading && (
          <div className="flex items-center gap-2 print:hidden">
            <button onClick={selectAll} className="text-[11px] text-primary hover:underline">
              {selectedIds.size === displayed.length ? 'Deselect all' : `Select all ${displayed.length}`}
            </button>
            <span className="text-[10px] text-muted-foreground">· Native/manual jobs only · Source-owned items managed elsewhere</span>
          </div>
        )}

        {/* Task list */}
        {loading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-xl">
            <CheckSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">{hasActiveFilters || searchQuery ? 'No jobs match your filters' : 'No scheduled jobs yet'}</p>
            <p className="text-xs mt-1">{hasActiveFilters || searchQuery ? 'Try adjusting your filters or search.' : 'Click "Add Job" to create a recurring task.'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {displayed.map(task => (
              <TaskCard key={task.id} task={task} isManager={isManager}
                selected={selectedIds.has(task.id)}
                onToggle={() => toggleSelect(task.id)}
                onComplete={() => load()}
                onEdit={openEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        {/* Print-only summary */}
        <div className="hidden print:block mt-8 pt-4 border-t text-xs text-muted-foreground">
          <p>The Rattlebone — Scheduled Jobs (Native/Manual) · Printed {new Date().toLocaleDateString('en-GB')}</p>
          <p>{counts.total} total · {counts.overdue} overdue · {counts.dueToday} due today · {counts.upToDate} up to date</p>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={open => { setFormOpen(open); if (!open) { setEditingTask(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingTask ? 'Edit Job' : 'Add Scheduled Job'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Task Name *</label>
              <Input value={form.taskName} onChange={e => setForm(f => ({ ...f, taskName: e.target.value }))} placeholder="e.g. Deep clean walk-in fridge" autoFocus />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Description</label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Details…" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Frequency</label>
                <Select value={form.frequency} onValueChange={v => setForm(f => ({ ...f, frequency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQUENCIES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.frequency === 'Weekly' && (
                <div>
                  <label className="text-xs text-muted-foreground mb-1.5 block">Day of Week</label>
                  <Select value={form.dayOfWeek || '_any'} onValueChange={v => setForm(f => ({ ...f, dayOfWeek: v === '_any' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Any day" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_any">Any day</SelectItem>
                      {DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Category</label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Priority</label>
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Lead Time (days)</label>
                <Input type="number" min={0} max={30} value={form.leadTimeDays} onChange={e => setForm(f => ({ ...f, leadTimeDays: parseInt(e.target.value) || 0 }))} />
                <p className="text-[9px] text-muted-foreground mt-0.5">Reminder days before due</p>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Assigned To</label>
              <Input value={form.assignedToName} onChange={e => setForm(f => ({ ...f, assignedToName: e.target.value }))} placeholder="Name…" />
            </div>
            <div className="border border-border rounded-lg p-3 space-y-3">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wide">Reminders</p>
              <div className="flex items-center gap-3">
                <Switch id="email-sw" checked={form.sendEmail} onCheckedChange={v => setForm(f => ({ ...f, sendEmail: v }))} />
                <label htmlFor="email-sw" className="text-xs text-foreground">Email reminder</label>
              </div>
              {form.sendEmail && <Input type="email" value={form.reminderEmail} onChange={e => setForm(f => ({ ...f, reminderEmail: e.target.value }))} placeholder="reminder@email.com" className="text-xs" />}
              <div className="flex items-center gap-3">
                <Switch id="sms-sw" checked={form.sendSms} onCheckedChange={v => setForm(f => ({ ...f, sendSms: v }))} />
                <label htmlFor="sms-sw" className="text-xs text-foreground">SMS reminder</label>
              </div>
              {form.sendSms && <Input type="tel" value={form.reminderPhone} onChange={e => setForm(f => ({ ...f, reminderPhone: e.target.value }))} placeholder="+44 7700 000000" className="text-xs" />}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving || !form.taskName.trim()} className="flex-1">
                {saving ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Saving…</> : editingTask ? 'Update Job' : 'Add Job'}
              </Button>
              <Button variant="outline" onClick={() => { setFormOpen(false); setEditingTask(null); }}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Export Scheduled Jobs</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-muted-foreground">Native/manual jobs only. Source-owned items (gas, compliance, etc.) export from their own pages.</p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="flex-col h-20 gap-1.5" onClick={() => handleExport('pdf', 'due-now')} disabled={exporting}>
                <FileText className="w-5 h-5 text-destructive" /><span className="text-xs">Due Now PDF</span>
              </Button>
              <Button variant="outline" className="flex-col h-20 gap-1.5" onClick={() => handleExport('csv', 'due-now')} disabled={exporting}>
                <Download className="w-5 h-5 text-primary" /><span className="text-xs">Due Now CSV</span>
              </Button>
              <Button variant="outline" className="flex-col h-20 gap-1.5" onClick={() => handleExport('pdf', 'monthly')} disabled={exporting}>
                <Calendar className="w-5 h-5 text-primary" /><span className="text-xs">Monthly PDF</span>
              </Button>
              <Button variant="outline" className="flex-col h-20 gap-1.5" onClick={() => handleExport('csv', 'monthly')} disabled={exporting}>
                <Download className="w-5 h-5 text-primary" /><span className="text-xs">Monthly CSV</span>
              </Button>
            </div>
            <Button variant="outline" className="w-full gap-2 text-xs" onClick={() => { setExportOpen(false); window.print(); }}>
              <Printer className="w-3.5 h-3.5" /> Print Current View
            </Button>
            {exporting && <div className="flex items-center justify-center gap-2 py-2"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Generating…</span></div>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Email Report</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-muted-foreground">Native/manual jobs only.</p>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Recipient Email *</label>
              <Input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="manager@rattlebone.co.uk" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Notes</label>
              <Textarea value={emailNotes} onChange={e => setEmailNotes(e.target.value)} rows={2} placeholder="Optional notes…" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" size="sm" onClick={() => handleEmail('due-now')} disabled={emailSending || !emailTo.trim()} className="text-xs h-9 flex-col gap-0.5">
                {emailSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <>📋<span>Due Now</span></>}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleEmail('summary')} disabled={emailSending || !emailTo.trim()} className="text-xs h-9 flex-col gap-0.5">
                {emailSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <>📊<span>Summary</span></>}
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleEmail('monthly')} disabled={emailSending || !emailTo.trim()} className="text-xs h-9 flex-col gap-0.5">
                {emailSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <>📅<span>Monthly</span></>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
